import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Map } from '@base/models/responses/login-loader.res';
import { ModalService } from '@base/services/private/modal.service';
import { ModalHeaderComponent } from '@base/components/private/modals/modal-header/modal-header.component';
import { ModalBodyComponent } from '@base/components/private/modals/modal-body/modal-body.component';
import { GlobalStateService } from '@base/services/private/global-state.service';
import { lastValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { EventService } from '@base/services/private/event.service';
import { Floorplan } from '@base/models/responses/floorplan.res';
import { TranslatePipe } from '@base/pipes/private/translate.pipe';
import { GlobalService } from '@base/services/private/global.service';
import { DynamicImportLoaderService } from '@base/services/private/dynamic-import.service';
import { WhiteLabel } from '@config/whitelabel-keys.constant';
import { LoadMFESrvAbstract } from '@base/abstracts/services/load-mfe.abstract';

@Component({
  selector: 'app-modal-map-list',
  imports: [ModalHeaderComponent, ModalBodyComponent, forwardRef(() => TranslatePipe)],
  templateUrl: './modal-map-list.component.html',
  styleUrl: './modal-map-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalMapListComponent implements OnInit, OnDestroy {
  private readonly modalSrv = inject(ModalService);
  private readonly globalStateSrv = inject(GlobalStateService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly eventSrv = inject(EventService);
  private readonly globalSrv = inject(GlobalService);
  private readonly dynamicImportLoaderSrv = inject(DynamicImportLoaderService);
  private loadMfeSrv: LoadMFESrvAbstract | null = null;

  @Input() modalId = '';
  @Input() mapId: number | null = null;

  map: Map | undefined = undefined;

  eventName = this.eventSrv?.data?.name;
  isBrandedDomain = this.eventSrv?.data?.isBrandedDomain;
  eventDirPath = this.isBrandedDomain ? '/' : this.eventName;

  ngOnInit(): void {
    (async () => {
      await this.getGlobalState();
    })();
  }

  ngOnDestroy(): void {
    this.globalSrv.destroyModalRef(this.modalId);
  }

  /**
   * Retrieves the global state from the GlobalStateService.
   * Finds and sets the map based on the provided mapId and updates change detection.
   * Closes the modal and auto-selects the map if it has no sub-maps.
   * @returns Promise<void>
   */
  /**
   * Lazily loads the Informa MFE service and caches it for floorplan gate checks.
   * @returns Promise<void>
   */
  private async loadInformaMfeService(): Promise<void> {
    try {
      this.loadMfeSrv = await this.dynamicImportLoaderSrv.loadService<LoadMFESrvAbstract>(WhiteLabel.SERVICES.LoadMfe);
    } catch (error) {
      console.error('Failed to load Informa MFE service:', error);
    }
  }

  async getGlobalState(): Promise<void> {
    try {
      await this.loadInformaMfeService();
      const data = await lastValueFrom(this.globalStateSrv.getState$());
      if (data) {
        const maps = data.loader?.maps || [];
        const idx = maps!.findIndex((item) => item.map_id === this.mapId);
        if (idx > -1) {
          this.map = maps[idx] || [];
          this.changeDetectorRef.detectChanges();
          this.changeDetectorRef.markForCheck();

          if (this.map.sub_maps.length === 0) {
            // selectMap() closes this list before opening the selected floorplan flow.
            this.selectMap(this.map);
          }
        }
      }
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Closes the modal using the provided modalId.
   * @returns void
   */
  close(): void {
    this.modalSrv.close(this.modalId);
  }

  /**
   * Selects a map and navigates to a destination based on event capabilities.
   *
   * Priority:
   *  1. `has_company` → `meet/s/companies`
   *  2. `has_meet`    → `meet/s/people`
   *  3. `has_agenda`  → `agenda`
   *
   * Updates the global state with the selected map's floorplan details.
   * @param item - The map to be selected.
   * @returns void
   */
  selectMap(item?: Map) {
    if (item) {
      const pgId = this.globalStateSrv.getState()?.loader?.pg_id ?? '';
      if (this.loadMfeSrv?.canUseExternalFloorplan(pgId, this.globalStateSrv.isAnon())) {
        this.close();
        this.modalSrv.showModalExternalFloorplan({
          addsData: { floorplanId: item.map_id ?? null, markerId: null },
        });
        return;
      }
      this.close();
      if (this.globalStateSrv.isHasCompany()) {
        this.router.navigate([this.eventDirPath, 'meet', 's', 'companies'], { queryParamsHandling: 'preserve' });
      } else if (this.globalStateSrv.isHasMeet()) {
        this.router.navigate([this.eventDirPath, 'meet', 's', 'people'], { queryParamsHandling: 'preserve' });
      } else if (this.globalStateSrv.isHasAgenda()) {
        this.router.navigate([this.eventDirPath, 'agenda'], { queryParamsHandling: 'preserve' });
      } else {
        // Maps-only event: no company, meet, or agenda section to route into.
        // Stay on the current page; the marker detail below drives the map view.
        console.warn('Navigating from a maps-only event is not currently supported; staying on the current view.');
      }
      const data: Floorplan = {
        floorplan_id: item?.map_id,
        location_name: item?.title,
        marker_id: -1,
        action: '',
        source: 'explore',
      };
      this.globalStateSrv.setMarkerDetail(data);
    }
  }
}
